import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Send, Sparkles, Save, Loader2, MessageSquare, Plus } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { format } from 'date-fns';

export default function ResearchChatTab({ projectId }) {
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);
  const messagesEndRef = useRef(null);
  const { getAuthHeaders, API } = useAuth();

  useEffect(() => {
    fetchSessions();
  }, [projectId]);

  useEffect(() => {
    if (activeSession) {
      fetchMessages(activeSession.id);
    }
  }, [activeSession]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/chat/sessions/${projectId}`, getAuthHeaders());
      setSessions(response.data);
      if (response.data.length > 0 && !activeSession) {
        setActiveSession(response.data[0]);
      }
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/chat/messages/${sessionId}`, getAuthHeaders());
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const createNewSession = async () => {
    try {
      const response = await axios.post(`${API}/chat/sessions/${projectId}`, {}, getAuthHeaders());
      setSessions([response.data, ...sessions]);
      setActiveSession(response.data);
      setMessages([]);
    } catch (error) {
      console.error('Failed to create session:', error);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || !activeSession || sending) return;

    const userMessage = input.trim();
    setInput('');
    setSending(true);

    try {
      const response = await axios.post(
        `${API}/chat/messages/${activeSession.id}`,
        { content: userMessage },
        getAuthHeaders()
      );

      setMessages([...messages, response.data.user_message, response.data.assistant_message]);
      
      // Update session in list
      const updatedSessions = sessions.map(s => 
        s.id === activeSession.id 
          ? { ...s, last_message_at: new Date().toISOString(), message_count: (s.message_count || 0) + 2 }
          : s
      );
      setSessions(updatedSessions);

    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setSending(false);
    }
  };

  const saveAsConversation = async () => {
    if (!activeSession || saving) return;
    
    setSaving(true);
    try {
      await axios.post(
        `${API}/chat/sessions/${activeSession.id}/save-as-conversation`,
        {},
        getAuthHeaders()
      );
      alert('Chat saved as conversation! AI is now extracting insights.');
    } catch (error) {
      console.error('Failed to save:', error);
      alert('Failed to save conversation');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 h-[calc(100vh-250px)]">
      {/* Sessions Sidebar */}
      <div className="lg:col-span-1">
        <div className="card p-4 h-full flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-semibold text-slate-900">Chat Sessions</h3>
            <Button
              onClick={createNewSession}
              size="sm"
              className="btn-primary"
              data-testid="new-chat-session-button"
            >
              <Plus size={16} />
            </Button>
          </div>
          
          <div className="flex-1 overflow-y-auto space-y-2">
            {loading ? (
              <div className="text-center py-8 text-slate-500">Loading...</div>
            ) : sessions.length === 0 ? (
              <div className="text-center py-8">
                <MessageSquare className="mx-auto mb-2 text-slate-300" size={32} />
                <p className="text-sm text-slate-500">No chats yet</p>
                <Button onClick={createNewSession} className="btn-primary mt-3" size="sm">
                  Start Research
                </Button>
              </div>
            ) : (
              sessions.map((session) => (
                <div
                  key={session.id}
                  onClick={() => setActiveSession(session)}
                  className={`p-3 rounded-md cursor-pointer transition-colors ${
                    activeSession?.id === session.id
                      ? 'bg-blue-50 border border-blue-200'
                      : 'bg-slate-50 hover:bg-slate-100'
                  }`}
                  data-testid="chat-session-item"
                >
                  <div className="text-sm font-medium text-slate-900 line-clamp-2 mb-1">
                    {session.title}
                  </div>
                  <div className="text-xs text-slate-500">
                    {session.message_count || 0} messages · {format(new Date(session.last_message_at), 'MMM dd')}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Chat Area */}
      <div className="lg:col-span-3">
        <div className="card h-full flex flex-col">
          {activeSession ? (
            <>
              {/* Header */}
              <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sparkles className="text-blue-600" size={20} />
                  <h3 className="font-heading font-semibold text-slate-900">{activeSession.title}</h3>
                </div>
                <Button
                  onClick={saveAsConversation}
                  disabled={saving || messages.length === 0}
                  size="sm"
                  className="btn-secondary flex items-center gap-2"
                  data-testid="save-conversation-button"
                >
                  {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                  Save as Conversation
                </Button>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-6 space-y-4" data-testid="chat-messages-container">
                {messages.length === 0 ? (
                  <div className="text-center py-20">
                    <Sparkles className="mx-auto mb-4 text-blue-300" size={48} />
                    <h4 className="text-lg font-medium text-slate-900 mb-2">Start Your Research</h4>
                    <p className="text-slate-600 mb-4">Ask Claude Sonnet 4.6 anything about companies, markets, or investments</p>
                    <div className="text-sm text-slate-500 space-y-1">
                      <p>💡 "Analyze the Series B SaaS market trends"</p>
                      <p>💡 "What are the key risks in enterprise AI startups?"</p>
                      <p>💡 "Compare ARR multiples across different verticals"</p>
                    </div>
                  </div>
                ) : (
                  messages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      data-testid={`chat-message-${message.role}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-lg p-4 ${
                          message.role === 'user'
                            ? 'bg-blue-600 text-white'
                            : 'bg-slate-100 text-slate-900'
                        }`}
                      >
                        <div className="text-xs opacity-70 mb-1">
                          {message.user_name} · {format(new Date(message.created_at), 'h:mm a')}
                        </div>
                        <div className="whitespace-pre-wrap text-sm leading-relaxed">{message.content}</div>
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <form onSubmit={sendMessage} className="p-4 border-t border-slate-200">
                <div className="flex gap-3">
                  <Textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Ask Claude about your investment research..."
                    rows={2}
                    className="flex-1"
                    disabled={sending}
                    data-testid="chat-input"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage(e);
                      }
                    }}
                  />
                  <Button
                    type="submit"
                    disabled={!input.trim() || sending}
                    className="btn-primary self-end"
                    data-testid="chat-send-button"
                  >
                    {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                  </Button>
                </div>
                <div className="text-xs text-slate-500 mt-2">
                  Press Enter to send, Shift+Enter for new line
                </div>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MessageSquare className="mx-auto mb-4 text-slate-300" size={64} />
                <h4 className="text-lg font-medium text-slate-900 mb-2">No Chat Selected</h4>
                <p className="text-slate-600 mb-4">Create or select a chat session to start</p>
                <Button onClick={createNewSession} className="btn-primary">
                  <Plus size={18} className="mr-2" />
                  New Research Chat
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
